import { useEffect, useState, type FormEvent } from 'react';
import { useContentBriefQuery, useSaveContentBrief } from '../../api/queries';
import { showToast } from '../../components/Toast';

type Props = { af: string };

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

/**
 * The annual territory brief.
 *
 * Deliberately one large free-text box plus a small block of checkable facts,
 * rather than a long structured form. The pipeline step this feeds wants raw,
 * messy prose — it distils the angle itself and rejects the table-stakes parts.
 * Splitting the answer into tidy fields here would do that job badly and throw
 * away the material it reads.
 */
export default function ContentBriefSubTab({ af }: Props) {
  const q = useContentBriefQuery(af);
  const save = useSaveContentBrief(af);

  const [notes, setNotes] = useState('');
  const [stateLicense, setStateLicense] = useState('');
  const [certifications, setCertifications] = useState('');
  const [credentials, setCredentials] = useState('');
  const [awards, setAwards] = useState('');
  const [memberships, setMemberships] = useState('');
  const [bondedInsured, setBondedInsured] = useState(false);
  const [factsAttested, setFactsAttested] = useState(false);
  const [doNotMention, setDoNotMention] = useState('');
  const [faqQuestions, setFaqQuestions] = useState('');
  const [loaded, setLoaded] = useState(false);

  // Seed the form once from the server, then leave it alone — refetches must not
  // stomp on what the user is part-way through typing.
  useEffect(() => {
    if (loaded || !q.data) return;
    const b = q.data.brief;
    if (b) {
      setNotes(b.notes ?? '');
      setStateLicense(b.stateLicense ?? '');
      setCertifications(b.certifications ?? '');
      setCredentials(b.credentials ?? '');
      setAwards(b.awards ?? '');
      setMemberships(b.memberships ?? '');
      setBondedInsured(b.bondedInsured);
      setFactsAttested(b.factsAttested);
      setDoNotMention(b.doNotMention ?? '');
      setFaqQuestions(b.faqQuestions ?? '');
    }
    setLoaded(true);
  }, [q.data, loaded]);

  async function submitBrief(e: FormEvent, submit: boolean) {
    e.preventDefault();
    try {
      await save.mutateAsync({
        notes, stateLicense, certifications, credentials, awards, memberships,
        bondedInsured, factsAttested, doNotMention, faqQuestions, submit,
      });
      showToast(submit ? 'Brief submitted — thank you' : 'Draft saved', { variant: 'success' });
    } catch (err) {
      showToast((err as Error).message, { variant: 'error' });
    }
  }

  if (q.isLoading) {
    return (
      <div className="listings-loading">
        <div className="spinner" />
        Loading…
      </div>
    );
  }

  const campaign = q.data?.campaign ?? null;
  const brief = q.data?.brief ?? null;

  if (!campaign) {
    return (
      <div className="tab-placeholder">
        <h3>No brief open right now</h3>
        <p>
          Once a year we ask what's changed in your territory, and use it to refresh
          the content on your local pages. We'll let you know when the next one opens.
        </p>
      </div>
    );
  }

  const locked = brief?.status === 'used';
  const busy = save.isPending;

  return (
    <form className="content-form" onSubmit={(e) => submitBrief(e, true)}>
      <h3>{campaign.title}</h3>
      {campaign.intro && <p className="content-intro">{campaign.intro}</p>}
      {campaign.closesAt && (
        <p className="content-intro">Open until {fmtDate(campaign.closesAt)}.</p>
      )}

      {brief?.status === 'submitted' && (
        <div className="schema-warning">
          Submitted {fmtDate(brief.submittedAt)}. You can still edit until the window closes.
        </div>
      )}
      {locked && (
        <div className="schema-warning">
          This brief has been used to refresh your pages, so it's now locked as the
          record of what they were written from.
        </div>
      )}

      <label>
        What's changed in your territory?
        <span className="field-help">
          New services, partnerships, staff, community involvement, anything you're
          known for locally. Don't worry about structure — bullet points, a rough
          brain-dump, or a forwarded email all work. Longer and messier is better
          than short and polished.
        </span>
        <textarea
          rows={14}
          value={notes}
          disabled={locked}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. We brought on a dementia-certified care manager in March; we've been sponsoring the Westfield senior centre's Friday lunch since last autumn…"
        />
      </label>

      <fieldset className="content-fieldset">
        <legend>Credentials and accreditations</legend>
        <p className="field-help">
          Anything here may be stated on your pages, so only include what's accurate
          and current. Leave blank to claim nothing.
        </p>

        <label>
          State licence number
          <input value={stateLicense} disabled={locked}
                 onChange={(e) => setStateLicense(e.target.value)}
                 placeholder="e.g. Ohio HCS licence #1234" />
        </label>
        <label>
          Certifications <span className="field-help">One per line</span>
          <textarea rows={3} value={certifications} disabled={locked}
                    onChange={(e) => setCertifications(e.target.value)} />
        </label>
        <label>
          Owner / staff credentials <span className="field-help">One per line</span>
          <textarea rows={3} value={credentials} disabled={locked}
                    onChange={(e) => setCredentials(e.target.value)} />
        </label>
        <label>
          Awards <span className="field-help">One per line</span>
          <textarea rows={2} value={awards} disabled={locked}
                    onChange={(e) => setAwards(e.target.value)} />
        </label>
        <label>
          Memberships <span className="field-help">One per line</span>
          <textarea rows={2} value={memberships} disabled={locked}
                    onChange={(e) => setMemberships(e.target.value)} />
        </label>
        <label className="checkbox-row">
          <input type="checkbox" checked={bondedInsured} disabled={locked}
                 onChange={(e) => setBondedInsured(e.target.checked)} />
          We are bonded and insured
        </label>
        <label className="checkbox-row">
          <input type="checkbox" checked={factsAttested} disabled={locked}
                 onChange={(e) => setFactsAttested(e.target.checked)} />
          I confirm the credentials above are accurate and current
        </label>
      </fieldset>

      <label>
        Anything we should NOT mention?
        <span className="field-help">
          Services you don't offer, or claims you'd rather we didn't make. These are
          kept off your pages entirely.
        </span>
        <textarea rows={3} value={doNotMention} disabled={locked}
                  onChange={(e) => setDoNotMention(e.target.value)}
                  placeholder="e.g. We do not offer skilled nursing or medication administration." />
      </label>

      <label>
        Questions you're often asked
        <span className="field-help">
          One per line. We'll answer the ones that fit on your pages.
        </span>
        <textarea rows={3} value={faqQuestions} disabled={locked}
                  onChange={(e) => setFaqQuestions(e.target.value)} />
      </label>

      {!locked && (
        <div className="content-actions">
          <button type="button" className="secondary-btn" disabled={busy}
                  onClick={(e) => submitBrief(e, false)}>
            Save draft
          </button>
          <button type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Submit brief'}
          </button>
        </div>
      )}
    </form>
  );
}
